import { getLoanApplicationById } from '@nemuru-eng/js-api-client';
import {
  getProcessResponseByLoanApplicationId,
  postLoanApplicationInLender,
  ProcessResponse,
} from '@nemuru-eng/js-api-client/slim';
import { useState } from 'react';

import { useCache } from '@/hooks/requests/useCache';

interface UseLoanApplicationProps {
  loanApplicationId: string;
  loanId: string;
}

export const useLoanApplication = ({ loanApplicationId }: UseLoanApplicationProps) => {
  const { data: loanApplication, revalidate } = useCache(getLoanApplicationById, { loanApplicationId });

  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loadingProcessResult, setLoadingProcessResult] = useState(false);
  const [error, setError] = useState<Error | undefined>();
  const [processResult, setProcessResult] = useState<ProcessResponse | undefined>();

  const processLoanApplication = async () => {
    setError(undefined);
    setProcessing(true);
    try {
      await postLoanApplicationInLender({ loanApplicationId });
      await revalidate();
      await getProcessResponse();
      setProcessing(false);
      setSuccess(true);
    } catch (e) {
      setError(e as Error);
    }
    setProcessing(false);
  };

  const closeAlerts = () => {
    setError(undefined);
    setSuccess(false);
  };

  const getProcessResponse = async () => {
    setLoadingProcessResult(true);
    const result: ProcessResponse = await getProcessResponseByLoanApplicationId({ loanApplicationId });
    if (result) {
      setProcessResult(parseProcessResult(result));
    }
    setLoadingProcessResult(false);
  };

  const parseProcessResult = (processResponse?: ProcessResponse): ProcessResponse | undefined => {
    if (!processResponse) return;
    let message = processResponse.message;
    if (processResponse.message) {
      try {
        message = JSON.parse(processResponse.message);
      } catch (e) {
        if (message && message.includes('Message:')) {
          try {
            message = JSON.parse(message.replace('Message:', ''));
            if (message && Object.keys(message).includes('schema') && typeof message['schema'] === 'string') {
              message['schema'] = JSON.parse(message['schema']);
            }
          } catch (e) {}
        }
      }
    }
    return { ...processResponse, message };
  };

  return {
    loanApplication,
    processing,
    error,
    success,
    closeAlerts,
    loadingProcessResult,
    processLoanApplication,
    getProcessResponse,
    processResult,
  };
};
